import { join, dirname, resolve } from 'node:path';
import type {
  EmbeddedScriptConfig,
  CustomElementConfig,
  BlocksWidgetConfig,
  EmbeddedScriptParamsOverride,
  SitePluginConfig,
  BlocksWidgetPanelConfig,
  SiteComponentConfig,
  SiteComponentPanelConfig,
  SiteComponentElementItem,
} from '@wix/cli-app-definitions';
import {
  PRESETS_DIR,
  ELEMENTS_DIR,
  embeddedScriptConfigSchema,
  customElementConfigSchema,
  blocksWidgetConfigSchema,
  embeddedScriptParamsOverrideSchema,
  sitePluginConfigSchema,
  blocksWidgetPanelConfigSchema,
  siteComponentConfigSchema,
  siteComponentPanelConfigSchema,
  siteComponentElementItemSchema,
} from '@wix/cli-app-definitions';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { pathExists, readJson } from '@wix/cli-fs';
import { isType } from 'variant';
import { ElementType } from '@wix/ambassador-devcenter-apps-v1-app-old/types';
import { replaceExtension } from '../path.js';
import type { ProjectModel } from '../../../model.js';
import type {
  SiteComponentWithSdk,
  SiteComponentWithPreview,
} from '../dev-center-component.js';
import { DevCenterComponent } from '../dev-center-component.js';

export async function loadEmbeddedScriptComponentConfig(
  embeddedScriptPath: string
): Promise<EmbeddedScriptConfig> {
  const configPath = replaceExtension(embeddedScriptPath, 'json');
  const isConfigExist = await pathExists(configPath);

  if (!isConfigExist) {
    throw new CliError({
      code: CliErrorCode.EmbeddedScriptConfigNotFound({ configPath }),
      cause: null,
    });
  }

  const config = await readJson(configPath);
  const parsedConfig = embeddedScriptConfigSchema.safeParse(config);

  if (parsedConfig.success) {
    return parsedConfig.data;
  }

  throw new CliError({
    code: CliErrorCode.InvalidConfigSchemaError({
      configPath,
      zodError: parsedConfig.error,
    }),
    cause: parsedConfig.error,
  });
}

export async function loadCustomElementComponentConfig(
  customElementPath: string
): Promise<CustomElementConfig> {
  const configPath = replaceExtension(customElementPath, 'json');
  const isConfigExist = await pathExists(configPath);

  if (!isConfigExist) {
    throw new CliError({
      code: CliErrorCode.CustomElementConfigNotFound({ configPath }),
      cause: null,
    });
  }

  const config = await readJson(configPath);
  const parsedConfig = customElementConfigSchema.safeParse(config);

  if (parsedConfig.success) {
    return parsedConfig.data;
  }

  throw new CliError({
    code: CliErrorCode.InvalidConfigSchemaError({
      configPath,
      zodError: parsedConfig.error,
    }),
    cause: parsedConfig.error,
  });
}

export async function loadSitePluginComponentConfig(
  sitePluginPath: string
): Promise<SitePluginConfig> {
  const configPath = replaceExtension(sitePluginPath, 'json');
  const isConfigExist = await pathExists(configPath);

  if (!isConfigExist) {
    throw new CliError({
      code: CliErrorCode.SitePluginConfigNotFound({ configPath }),
      cause: null,
    });
  }

  const config = await readJson(configPath);
  const parsedConfig = sitePluginConfigSchema.safeParse(config);

  if (parsedConfig.success) {
    return parsedConfig.data;
  }

  throw new CliError({
    code: CliErrorCode.InvalidConfigSchemaError({
      configPath,
      zodError: parsedConfig.error,
    }),
    cause: parsedConfig.error,
  });
}

export async function loadBlocksWidgetComponentConfig(
  blocksWidgetPath: string
): Promise<BlocksWidgetConfig> {
  const configPath = replaceExtension(blocksWidgetPath, 'json');
  const isConfigExist = await pathExists(configPath);

  if (!isConfigExist) {
    throw new CliError({
      code: CliErrorCode.BlocksWidgetConfigNotFound({ configPath }),
      cause: null,
    });
  }

  const config = await readJson(configPath);
  const parsedConfig = blocksWidgetConfigSchema.safeParse(config);

  if (parsedConfig.success) {
    return parsedConfig.data;
  }

  throw new CliError({
    code: CliErrorCode.InvalidConfigSchemaError({
      configPath,
      zodError: parsedConfig.error,
    }),
    cause: parsedConfig.error,
  });
}

export async function loadBlocksWidgetPanelComponentConfig(
  blocksWidgetPanelPath: string
): Promise<BlocksWidgetPanelConfig> {
  const configPath = replaceExtension(blocksWidgetPanelPath, 'json');
  const isConfigExist = await pathExists(configPath);

  if (!isConfigExist) {
    throw new CliError({
      code: CliErrorCode.BlocksWidgetPanelConfigNotFound({ configPath }),
      cause: null,
    });
  }

  const config = await readJson(configPath);
  const parsedConfig = blocksWidgetPanelConfigSchema.safeParse(config);

  if (parsedConfig.success) {
    return parsedConfig.data;
  }

  throw new CliError({
    code: CliErrorCode.InvalidConfigSchemaError({
      configPath,
      zodError: parsedConfig.error,
    }),
    cause: parsedConfig.error,
  });
}

export function virtualPathForCustomElementPanelHTMLAsset(componentId: string) {
  return `wix/html/custom-element-asset/${componentId}.html`;
}

export function virtualModuleIdForCustomElementPanelScriptAsset(
  componentId: string
) {
  return `wix/scripts/custom-element-asset/panel-${componentId}.js`;
}

export function virtualModuleIdForCustomElementScriptAsset(
  componentId: string
) {
  return `wix/scripts/custom-element-asset/${componentId}.js`;
}

export function virtualCustomElementPanelHTMLAssets(model: ProjectModel) {
  return new Map(
    model.components
      .filter(
        (
          component
        ): component is DevCenterComponent<'CustomElement' | 'SitePlugin'> =>
          (isType(component, DevCenterComponent.CustomElement) ||
            isType(component, DevCenterComponent.SitePlugin)) &&
          Boolean(component.relativePanelPath)
      )
      .map((comp) => [virtualPathForCustomElementPanelHTMLAsset(comp.id), comp])
  );
}

export function virtualCustomElementScriptAssets(model: ProjectModel) {
  return new Map(
    model.components
      .filter(
        (
          component
        ): component is DevCenterComponent<'CustomElement' | 'SitePlugin'> =>
          isType(component, DevCenterComponent.CustomElement) ||
          isType(component, DevCenterComponent.SitePlugin)
      )
      .map((comp) => [
        virtualModuleIdForCustomElementScriptAsset(comp.id),
        comp,
      ])
  );
}

export function virtualCustomElementPanelScriptAssets(model: ProjectModel) {
  return new Map(
    model.components
      .filter(
        (
          component
        ): component is DevCenterComponent<'CustomElement' | 'SitePlugin'> =>
          (isType(component, DevCenterComponent.CustomElement) ||
            isType(component, DevCenterComponent.SitePlugin)) &&
          Boolean(component.relativePanelPath)
      )
      .map((comp) => [
        virtualModuleIdForCustomElementPanelScriptAsset(comp.id),
        comp,
      ])
  );
}

export function virtualModuleIdForBlocksComponentScriptAsset(
  componentId: string
) {
  return `wix/scripts/blocks-component-asset/${componentId}.js`;
}

export function virtualBlocksWidgetPanelScriptAssets(model: ProjectModel) {
  return new Map(
    model.components
      .filter(isType(DevCenterComponent.BlocksWidgetPanel))
      .map((comp) => [
        virtualModuleIdForBlocksComponentScriptAsset(comp.id),
        comp,
      ])
  );
}

export function virtualBlocksWidgetScriptAssets(model: ProjectModel) {
  return new Map(
    model.components
      .filter(isType(DevCenterComponent.BlocksWidget))
      .map((comp) => [
        virtualModuleIdForBlocksComponentScriptAsset(comp.id),
        comp,
      ])
  );
}

export function virtualModuleIdForSiteComponentScriptAsset(
  componentId: string
) {
  return `wix/scripts/site-component-asset/${componentId}.js`;
}

export function virtualSiteComponentScriptAssets(model: ProjectModel) {
  return new Map(
    model.components
      .filter(isType(DevCenterComponent.SiteComponent))
      .map((comp) => [
        virtualModuleIdForSiteComponentScriptAsset(comp.id),
        comp,
      ])
  );
}

export function virtualModuleIdForSiteComponentPanelScriptAsset(
  componentId: string
) {
  return `wix/scripts/site-component-asset/panel-${componentId}.js`;
}

export function virtualSiteComponentPanelScriptAssets(model: ProjectModel) {
  return new Map(
    model.components
      .filter(isType(DevCenterComponent.SiteComponentPanel))
      .map((comp) => [
        virtualModuleIdForSiteComponentPanelScriptAsset(comp.id),
        comp,
      ])
  );
}
export function virtualModuleIdForSiteComponentPreview(componentId: string) {
  return `wix/scripts/site-component-asset/preview-${componentId}.js`;
}

export function virtualSiteComponentPreviewScriptAssets(model: ProjectModel) {
  const assets = new Map<string, SiteComponentWithPreview>();

  for (const comp of model.components) {
    if (
      isType(comp, DevCenterComponent.SiteComponent) &&
      comp.relativePreviewPath
    ) {
      assets.set(virtualModuleIdForSiteComponentPreview(comp.id), {
        ...comp,
        relativePreviewPath: comp.relativePreviewPath,
      });
    }
  }

  return assets;
}

export function virtualPresetIdForSiteComponent(
  componentId: string,
  presetFolder: string
) {
  return `${componentId}-preset-${presetFolder}.css`;
}

export function virtualPresetIdForSiteComponentElement(
  componentId: string,
  elements: string[],
  presetFolder: string
) {
  return `${componentId}${elements.reduce((acc, element) => acc.concat(`-${element}`), '')}-preset-${presetFolder}.css`;
}

function getElementsPresetEntries(
  componentId: string,
  componentDir: string,
  projectFolder: string,
  elements: Record<string, SiteComponentElementItem>,
  previousElementsStructure: string[] = []
): Array<[string, string]> {
  const entries: Array<[string, string]> = [];

  for (const [elementName, elementConfig] of Object.entries(elements)) {
    const elementPath = join(
      ELEMENTS_DIR,
      previousElementsStructure.reduce(
        (acc, parent) => join(acc, parent, ELEMENTS_DIR),
        ''
      ),
      elementName
    );
    const elementsStructure = [...previousElementsStructure, elementName];

    if (elementConfig.elementType === ElementType.inlineElement) {
      const inlineElement = elementConfig.inlineElement;

      // Handle presets for this inline element
      if (inlineElement.presets) {
        for (const presetFolder in inlineElement.presets) {
          const assetPath = resolve(
            projectFolder,
            join(componentDir, elementPath, PRESETS_DIR, `${presetFolder}.css`)
          );

          entries.push([
            virtualPresetIdForSiteComponentElement(
              componentId,
              elementsStructure,
              presetFolder
            ),
            assetPath,
          ]);
        }
      }

      // Recursively handle nested elements
      if (inlineElement.elements) {
        entries.push(
          ...getElementsPresetEntries(
            componentId,
            componentDir,
            projectFolder,
            inlineElement.elements,
            elementsStructure
          )
        );
      }
    } else {
      const resolvedElement = elementConfig.refElement.resolvedElement;

      // Handle presets for resolved element
      if (resolvedElement?.presets) {
        for (const presetFolder in resolvedElement.presets) {
          const assetPath = resolve(
            projectFolder,
            join(componentDir, elementPath, PRESETS_DIR, `${presetFolder}.css`)
          );
          entries.push([
            virtualPresetIdForSiteComponentElement(
              componentId,
              elementsStructure,
              presetFolder
            ),
            assetPath,
          ]);
        }
      }

      // Recursively handle nested elements in resolved element
      if (resolvedElement?.elements) {
        entries.push(
          ...getElementsPresetEntries(
            componentId,
            componentDir,
            projectFolder,
            resolvedElement.elements,
            elementsStructure
          )
        );
      }
    }
  }

  return entries;
}

export function virtualSiteComponentPresetAssets(model: ProjectModel) {
  const entries: Array<[string, string]> = [];

  const siteComponents = model.components.filter(
    isType(DevCenterComponent.SiteComponent)
  );

  for (const component of siteComponents) {
    if (component.presets) {
      const componentDir = dirname(component.relativePath);
      for (const presetFolder in component.presets) {
        const assetPath = resolve(
          model.projectFolder,
          join(componentDir, PRESETS_DIR, `${presetFolder}.css`)
        );
        entries.push([
          virtualPresetIdForSiteComponent(component.id, presetFolder),
          assetPath,
        ]);
      }
    }
    if (Object.keys(component.elements).length) {
      const componentDir = dirname(component.relativePath);
      entries.push(
        ...getElementsPresetEntries(
          component.id,
          componentDir,
          model.projectFolder,
          component.elements
        )
      );
    }
  }

  return new Map(entries);
}

export function virtualModuleIdForSiteComponentSdkScriptAsset(
  componentId: string
) {
  return `wix/scripts/site-component-asset/sdk-${componentId}.js`;
}

export function virtualSiteComponentSdkScriptAssets(model: ProjectModel) {
  const assets = new Map<string, SiteComponentWithSdk>();

  for (const comp of model.components) {
    if (
      isType(comp, DevCenterComponent.SiteComponent) &&
      comp.relativeSdkPath
    ) {
      assets.set(virtualModuleIdForSiteComponentSdkScriptAsset(comp.id), {
        ...comp,
        relativeSdkPath: comp.relativeSdkPath,
      });
    }
  }

  return assets;
}

export async function loadSiteComponentElementConfig(
  basePath: string,
  elementPath: string
) {
  const config = await readJson(join(basePath, elementPath));
  const parsedConfig = siteComponentElementItemSchema.safeParse(config);

  if (parsedConfig.success) {
    return parsedConfig.data;
  }

  throw new CliError({
    code: CliErrorCode.InvalidSiteComponentElementItem({
      elementPath,
    }),
    cause: parsedConfig.error,
  });
}

export async function loadSiteComponentConfig(
  projectFolder: string,
  relativePath: string
): Promise<SiteComponentConfig> {
  const configRelativePath = replaceExtension(relativePath, 'json');
  const configPath = join(projectFolder, configRelativePath);
  const isConfigExist = await pathExists(configPath);

  if (!isConfigExist) {
    throw new CliError({
      code: CliErrorCode.SiteComponentConfigNotFound({
        configPath: configRelativePath,
      }),
      cause: null,
    });
  }

  const config = await readJson(configPath);
  const parsedConfig = siteComponentConfigSchema.safeParse(config);

  if (parsedConfig.success) {
    return parsedConfig.data;
  }

  throw new CliError({
    code: CliErrorCode.InvalidConfigSchemaError({
      configPath: configRelativePath,
      zodError: parsedConfig.error,
    }),
    cause: parsedConfig.error,
  });
}

export async function loadSiteComponentPanelConfig(
  projectFolder: string,
  relativePath: string
): Promise<SiteComponentPanelConfig> {
  const configRelativePath = replaceExtension(relativePath, 'json');
  const configPath = join(projectFolder, configRelativePath);
  const isConfigExist = await pathExists(configPath);

  if (!isConfigExist) {
    throw new CliError({
      code: CliErrorCode.SiteComponentPanelConfigNotFound({
        configPath: configRelativePath,
      }),
      cause: null,
    });
  }

  const config = await readJson(configPath);
  const parsedConfig = siteComponentPanelConfigSchema.safeParse(config);

  if (parsedConfig.success) {
    return parsedConfig.data;
  }

  throw new CliError({
    code: CliErrorCode.InvalidConfigSchemaError({
      configPath: configRelativePath,
      zodError: parsedConfig.error,
    }),
    cause: parsedConfig.error,
  });
}

export async function loadEmbeddedScriptParamsOverride(
  embeddedScriptPath: string
): Promise<EmbeddedScriptParamsOverride | null> {
  const paramsOverridePath = join(
    dirname(embeddedScriptPath),
    'params.dev.json'
  );
  const isParamsOverrideExist = await pathExists(paramsOverridePath);

  if (!isParamsOverrideExist) {
    return null;
  }

  const paramsOverride = await readJson(paramsOverridePath);
  const parsedConfig =
    embeddedScriptParamsOverrideSchema.safeParse(paramsOverride);

  if (parsedConfig.success) {
    return parsedConfig.data;
  }

  throw new CliError({
    code: CliErrorCode.InvalidParamsOverrideSchemaError({
      paramsOverridePath,
      zodError: parsedConfig.error,
    }),
    cause: parsedConfig.error,
  });
}
